import React, { ChangeEvent, DragEvent, FC, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Button, Header, Icon, Message, Modal } from 'semantic-ui-react';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { setAIContextDragData } from './AIWindow';
import SharepointLinkDialog, { getSafeExternalLink, SharepointBadge } from './SharepointLinkDialog';

interface DocumentVersionMiniProps {
    clientId: string;
    documentTypeId?: string;
    sowId?: string;
    clientOfferingId?: string;
    documentItemId?: string;
    LabelName?: string;
    FileDisplayName?: string;
    onDocumentItemIdCreated?: ( documentItemId: string ) => Promise<void> | void;
    showCreateItemRow?: boolean;
}

interface DocumentVersionRow {
    FileName?: string;
    DocumentItemName?: string;
    DocumentName?: string;
    DocumentExtension?: string;
    VersionNumber?: number;
    DocumentId?: string;
    DocumentVersionId?: string;
    DocumentItemId?: string;
    IsActiveDocumentVersion?: number;
    CompletedByConsultantName?: string;
    CompletedDate?: string;
    SharepointLink?: string;
    Versions?: DocumentVersionRow[];
    LabelName?: string;
}

interface PendingUpload {
    file: File;
    targetDocumentItemId?: string;
    requireDocumentName: boolean;
}

const getCaseInsensitiveValue = <T = unknown>( obj: unknown, key: string ): T | undefined => {
    if( !obj || typeof obj !== 'object' ) return undefined;
    const record = obj as Record<string, unknown>;
    const foundKey = Object.keys( record ).find( k => k.toLowerCase() === key.toLowerCase() );
    return foundKey ? ( record[ foundKey ] as T ) : undefined;
};

const parseArrayPayload = ( response: any ): any[] => {
    if( Array.isArray( response ) ) return response;

    const candidates = [ response?.result, response?.Result, response?.data, response?.Data ];
    for( const candidate of candidates ){
        if( Array.isArray( candidate ) ) return candidate;
        if( typeof candidate === 'string' ){
            try {
                const parsed = JSON.parse( candidate );
                if( Array.isArray( parsed ) ) return parsed;
            } catch {
                // ignore malformed JSON wrappers
            }
        }
    }

    if( typeof response === 'string' ){
        try {
            const parsed = JSON.parse( response );
            if( Array.isArray( parsed ) ) return parsed;
        } catch {
            // ignore malformed JSON payload
        }
    }

    return [];
};

const normalizeVersionRow = ( row: unknown, fallbackDocumentItemId?: string ): DocumentVersionRow => {
    return {
        FileName: getCaseInsensitiveValue<string>( row, 'FileName' ),
        DocumentItemName: getCaseInsensitiveValue<string>( row, 'DocumentItemName' ),
        DocumentName: getCaseInsensitiveValue<string>( row, 'DocumentName' ),
        DocumentExtension: getCaseInsensitiveValue<string>( row, 'DocumentExtension' ),
        VersionNumber: getCaseInsensitiveValue<number>( row, 'VersionNumber' ),
        DocumentId: getCaseInsensitiveValue<string>( row, 'DocumentId' ),
        DocumentVersionId: getCaseInsensitiveValue<string>( row, 'DocumentVersionId' ),
        DocumentItemId: getCaseInsensitiveValue<string>( row, 'DocumentItemId' ) || fallbackDocumentItemId,
        IsActiveDocumentVersion: getCaseInsensitiveValue<number>( row, 'IsActiveDocumentVersion' ),
        CompletedByConsultantName: getCaseInsensitiveValue<string>( row, 'CompletedByConsultantName' ),
        CompletedDate: getCaseInsensitiveValue<string>( row, 'CompletedDate' ),
        SharepointLink: getSafeExternalLink( getCaseInsensitiveValue<string>( row, 'SharepointLink' ) ),
    };
};

const normalizeRows = ( payload: any[], defaultDocumentItemId?: string ): DocumentVersionRow[] => {
    return payload.map( row => {
        const currentDocumentItemId = getCaseInsensitiveValue<string>( row, 'DocumentItemId' ) || defaultDocumentItemId;
        const rawVersions = getCaseInsensitiveValue<unknown>( row, 'Versions' );
        const nestedVersions = Array.isArray( rawVersions )
            ? rawVersions
            : typeof rawVersions === 'string'
                ? parseArrayPayload( rawVersions )
                : [];

        return {
            ...normalizeVersionRow( row, currentDocumentItemId ),
            Versions: nestedVersions.map( version => normalizeVersionRow( version, currentDocumentItemId ) ),
        };
    } );
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const formatUpdatedUtc = ( value?: string ): string => {
    if( !value ) return 'Unknown';

    const trimmed = value.trim();

    // Date-only values should be treated as a calendar date, not UTC midnight.
    // Parsing "YYYY-MM-DD" with Date() can shift a day for users behind UTC.
    if( DATE_ONLY_PATTERN.test( trimmed ) ){
        const [ yearText, monthText, dayText ] = trimmed.split( '-' );
        const year = Number( yearText );
        const month = Number( monthText );
        const day = Number( dayText );

        if( Number.isFinite( year ) && Number.isFinite( month ) && Number.isFinite( day ) ){
            return new Date( year, month - 1, day ).toLocaleDateString();
        }
    }

    const parsed = new Date( trimmed );
    if( Number.isNaN( parsed.getTime() ) ) return value;

    return parsed.toLocaleDateString();
};

const triggerBrowserDownload = ( blob: Blob, fileName: string ) => {
    const href = URL.createObjectURL( blob );
    const anchor = document.createElement( 'a' );
    anchor.href = href;
    anchor.download = fileName;
    document.body.appendChild( anchor );
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL( href );
};

const formatFileSize = ( sizeInBytes: number ): string => {
    if( sizeInBytes < 1024 ) return `${ sizeInBytes } B`;

    const units = [ 'KB', 'MB', 'GB', 'TB' ];
    let value = sizeInBytes / 1024;
    let unitIndex = 0;

    while( value >= 1024 && unitIndex < units.length - 1 ){
        value /= 1024;
        unitIndex += 1;
    }

    return `${ value.toFixed( 2 ) } ${ units[ unitIndex ] }`;
};

const getDefaultDocumentName = ( fileName: string ): string => {
    const trimmed = fileName.trim();
    const lastDotIndex = trimmed.lastIndexOf( '.' );

    // Keep original name when there is no extension (or only a leading dot like ".env").
    if( lastDotIndex <= 0 || lastDotIndex === trimmed.length - 1 ) return trimmed;

    return trimmed.slice( 0, lastDotIndex );
};

/** Document.Extension is stored without a leading dot (e.g. "docx"). */
const normalizeExtension = ( extension?: string ): string =>
    ( extension || '' ).trim().replace( /^\.+/, '' );

/** Document.FileName is the full original file name, so strip the extension when it's already present. */
const getFileBaseName = ( fileName: string, extension: string ): string => {
    const suffix = `.${ extension }`;
    return extension && fileName.toLowerCase().endsWith( suffix.toLowerCase() )
        ? fileName.slice( 0, -suffix.length )
        : fileName;
};

const getDocumentFileParts = ( item: DocumentVersionRow ): { name: string; extension: string } => {
    const extension = normalizeExtension( item.DocumentExtension );
    const fileName  = item.FileName || item.DocumentName || item.DocumentItemName || item.DocumentId || '';
    return { name: getFileBaseName( fileName, extension ), extension };
};

const DocumentVersionMini: FC<DocumentVersionMiniProps> = ( {
    clientId,
    documentTypeId,
    sowId,
    clientOfferingId,
    documentItemId,
    LabelName,
    FileDisplayName,
    onDocumentItemIdCreated,
    showCreateItemRow = true,
} ) => {
    const {
        executeStoredProcedure,
        RunInsert,
        RunUpdate,
        TableSelect,
        uploadDocumentToStorage,
        downloadDocumentFromStorage,
        addSharepointLinkVersion,
    } = useCentralCalls();

    const [ documents, setDocuments ]               = useState<DocumentVersionRow[]>( [] ),
          [ loading, setLoading ]                   = useState( false ),
          [ loadingError, setLoadingError ]         = useState( '' ),
          [ downloadingId, setDownloadingId ]       = useState<string | null>( null ),
          [ deletingId, setDeletingId ]             = useState<string | null>( null ),
          [ versionsOpenItem, setVersionsOpenItem ] = useState<DocumentVersionRow | null>( null ),
          [ refreshKey, setRefreshKey ]             = useState(0),
          [ dragOverItemId, setDragOverItemId ]     = useState<string | null>( null );

    const [ pendingUpload, setPendingUpload ]             = useState<PendingUpload | null>( null ),
          [ isUploadConfirmOpen, setIsUploadConfirmOpen ] = useState( false ),
          [ isUploading, setIsUploading ]                 = useState( false ),
          [ documentName, setDocumentName ]               = useState( '' );

    // Target for the "Add SharePoint link" dialog; null while it's closed.
    const [ sharepointTarget, setSharepointTarget ] = useState<{ documentItemId?: string; requireDocumentName: boolean } | null>( null );

    const [ uploadError, setUploadError ] = useState( '' );
    const miniLabel = LabelName?.trim() || '';
    const fileDisplayName = FileDisplayName?.trim() || '';

    const fileInputRef                 = useRef<HTMLInputElement>( null ),
          browseTargetItemIdRef        = useRef<string | undefined>(undefined),
          browseRequireDocumentNameRef = useRef<boolean>( false );

    useEffect( () => {
        const canLoadByDocumentItem = !!documentItemId;
        const canLoadByOfferingContext = !!clientOfferingId && !!documentTypeId && !!sowId;

        if( !clientId || ( !canLoadByDocumentItem && !canLoadByOfferingContext ) ){
            console.log("CNACNELLLL LOAD", 'clientid:',clientId, 'documenitemid:',documentItemId,'clientoffeirngid:', clientOfferingId, 'documetTypeid:;', documentTypeId, 'sowid:', sowId);
            setDocuments( [] );
            setLoading( false );
            setLoadingError( '' );
            return;
        }

        let cancelled = false;

        const loadDocuments = async () => {
            setLoading( true );
            setLoadingError( '' );

            try {
                
                

                const response = await executeStoredProcedure(
                    'tng.sp_get_DocumentVersionsContent',
                    {sowId:sowId, clientOfferingId:clientOfferingId, documentTypeId:documentTypeId, clientId:clientId, documentItemId:documentItemId, showVersions:0}
                );
console.log('parms......',{sowId:sowId, clientOfferingId:clientOfferingId, documentTypeId:documentTypeId, clientId:clientId, documentItemId:documentItemId, showVersions:0})
                const payload = parseArrayPayload( response );
                const normalizedRows = normalizeRows( payload, documentItemId );

                console.groupCollapsed( '[DocumentVersionMini] sp_get_DocumentVersionsContent' );
         
                console.log( 'raw response', response );
                console.log( 'parsed payload', payload );
                console.log( 'normalized rows', normalizedRows );
                console.groupEnd();

                if( !cancelled ){
                    setDocuments( normalizedRows );
                }
            } catch( err ){
                const message = err instanceof Error ? err.message : 'Failed to load documents.';
                if( !cancelled ){
                    setLoadingError( message );
                    setDocuments( [] );
                }
            } finally {
                if( !cancelled ){
                    setLoading( false );
                }
            }
        };

        loadDocuments();

        return () => {
            cancelled = true;
        };
        // Avoid depending on unstable hook function references from useCentralCalls.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ clientId, clientOfferingId, documentTypeId, sowId, refreshKey, documentItemId ] );

    const openUploadConfirm = ( file: File, targetDocumentItemId?: string, requireDocumentName?: boolean ) => {
        const isNewDocumentItemUpload = !!requireDocumentName && !targetDocumentItemId && !documentItemId;

        setPendingUpload( {
            file,
            targetDocumentItemId,
            requireDocumentName: !!requireDocumentName,
        } );
        setDocumentName( isNewDocumentItemUpload ? getDefaultDocumentName( file.name ) : '' );
        setUploadError( '' );
        setIsUploadConfirmOpen( true );
    };

    const openFilePickerForTarget = ( targetDocumentItemId?: string, requireDocumentName?: boolean ) => {
        browseTargetItemIdRef.current = targetDocumentItemId;
        browseRequireDocumentNameRef.current = !!requireDocumentName;
        fileInputRef.current?.click();
    };

    const handleFileInputChange = ( event: ChangeEvent<HTMLInputElement> ) => {
        const file = event.target.files?.[ 0 ];
        if( file ){
            openUploadConfirm( file, browseTargetItemIdRef.current, browseRequireDocumentNameRef.current );
        }

        event.target.value = '';
    };

    const handleDragOver = ( event: DragEvent<HTMLDivElement>, targetDocumentItemId?: string ) => {
        // Only OS file drags are uploads; ignore in-app drags (e.g. a row headed to the AI context drop zone).
        if( !Array.from( event.dataTransfer.types ).includes( 'Files' ) ) return;
        event.preventDefault();
        if( targetDocumentItemId ){
            setDragOverItemId( targetDocumentItemId );
        }
    };

    const handleDragLeave = ( event: DragEvent<HTMLDivElement>, targetDocumentItemId?: string ) => {
        event.preventDefault();
        if( targetDocumentItemId && dragOverItemId === targetDocumentItemId ){
            setDragOverItemId( null );
        }
    };

    const handleDrop = ( event: DragEvent<HTMLDivElement>, targetDocumentItemId?: string ) => {
        event.preventDefault();
        setDragOverItemId( null );

        const file = event.dataTransfer.files?.[ 0 ];
        if( file ){
            openUploadConfirm( file, targetDocumentItemId, !targetDocumentItemId );
        }
    };

    const closeUploadConfirm = () => {
        if( isUploading ) return;
        setIsUploadConfirmOpen( false );
        setPendingUpload( null );
        setDocumentName( '' );
    };

    /** Inserts a new DocumentItem for this client/type (and offering, when set) and returns its id. */
    const createDocumentItem = async ( name: string ): Promise<string> => {
        const newDocumentItemId = uuid();

        const insertParameters: Record<string, string> = {
            Id: newDocumentItemId,
            ClientId: clientId,
            DocumentTypeId: documentTypeId || '',
            Name: name,
        };

        if( clientOfferingId?.trim() ){
            insertParameters.ClientOfferingId = clientOfferingId;
        }

        await RunInsert( 'DocumentItems', insertParameters );

        if( onDocumentItemIdCreated ){
            await onDocumentItemIdCreated( newDocumentItemId );
        }

        return newDocumentItemId;
    };

    const openSharepointDialog = ( targetDocumentItemId?: string ) => {
        setSharepointTarget( {
            documentItemId: targetDocumentItemId,
            requireDocumentName: !targetDocumentItemId && !documentItemId,
        } );
    };

    // Errors are thrown so the dialog stays open and shows them.
    const handleSubmitSharepointLink = async ( sharepointLink: string, newDocumentName: string ) => {
        if( !clientId?.trim() ) throw new Error( 'ClientId is required.' );

        let targetDocumentItemId = sharepointTarget?.documentItemId || documentItemId;
        if( !targetDocumentItemId ){
            if( !documentTypeId?.trim() ) throw new Error( 'DocumentTypeId is required to create a new document item.' );
            targetDocumentItemId = await createDocumentItem( newDocumentName );
        }

        await addSharepointLinkVersion( clientId, targetDocumentItemId, sharepointLink );
        setRefreshKey( prev => prev + 1 );
    };

    const handleConfirmUpload = async () => {
        if( !pendingUpload ) return;
        if( !clientId?.trim() ){
            setUploadError( 'ClientId is required.' );
            return;
        }

        if( pendingUpload.requireDocumentName && !documentName.trim() ){
            setUploadError( 'Document Name is required.' );
            return;
        }

        setIsUploading( true );
        setUploadError( '' );

        try {
            const targetDocumentItemId = pendingUpload.targetDocumentItemId || documentItemId;
            if( targetDocumentItemId ){
                console.log('existing document item id, uploading new version:', targetDocumentItemId);
                await uploadDocumentToStorage( clientId, targetDocumentItemId, pendingUpload.file );
            } else {
                if( !documentTypeId?.trim() ){
                    setUploadError( 'DocumentTypeId is required to create a new document item.' );
                    return;
                }

                const newDocumentItemId = await createDocumentItem( documentName.trim() || pendingUpload.file.name );
               console.log('INSERTED NEW ODUMCNETITEM...newDocumentItemId',newDocumentItemId);
                await uploadDocumentToStorage( clientId, newDocumentItemId, pendingUpload.file );
            }

            setIsUploadConfirmOpen( false );
            setPendingUpload( null );
            setDocumentName( '' );
            setRefreshKey( prev => prev + 1 );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Upload failed.';
            setUploadError( message );
        } finally {
            setIsUploading( false );
        }
    };

    // When the deleted document is the DocumentItem's active version, fall back to the
    // prior (lower VersionNumber) version if it still exists and isn't itself deleted.
    const reassignActiveVersion = async ( deletedVersion: DocumentVersionRow ) => {
        const documentItemId = deletedVersion.DocumentItemId;
        if( !documentItemId ) return;

        const siblingVersions = versionsOpenItem?.Versions || [];
        const priorVersion = siblingVersions
            .filter( v =>
                v.DocumentId !== deletedVersion.DocumentId
                && v.VersionNumber != null
                && deletedVersion.VersionNumber != null
                && v.VersionNumber < deletedVersion.VersionNumber
            )
            .sort( ( a, b ) => ( b.VersionNumber || 0 ) - ( a.VersionNumber || 0 ) )[ 0 ];

        let nextActiveDocumentVersionId: string | null = null;

        if( priorVersion?.DocumentId && priorVersion.DocumentVersionId ){
            const priorDocuments = await TableSelect( 'Documents', { Id: priorVersion.DocumentId } );
            const priorDocument = parseArrayPayload( priorDocuments )[ 0 ];
            const priorIsDeleted = !!getCaseInsensitiveValue<boolean>( priorDocument, 'IsDeleted' );

            if( priorDocument && !priorIsDeleted ){
                nextActiveDocumentVersionId = priorVersion.DocumentVersionId;
            }
        }

        await RunUpdate( 'DocumentItems', documentItemId, { ActiveDocumentVersionId: nextActiveDocumentVersionId } );
    };

    const handleDeleteVersion = async ( version: DocumentVersionRow ) => {
        if( !version.DocumentId ) return;

        const label = version.FileName || version.DocumentName || 'this document';
        if( !window.confirm( `Delete "${ label }"? This cannot be undone.` ) ) return;

        setDeletingId( version.DocumentId );
        setLoadingError( '' );

        try {
            await RunUpdate( 'Documents', version.DocumentId, {
                IsDeleted: true,
                DeletedUtc: new Date().toISOString(),
            } );

            if( version.IsActiveDocumentVersion === 1 ){
                await reassignActiveVersion( version );
            }

            setVersionsOpenItem( prev => prev ? {
                ...prev,
                Versions: ( prev.Versions || [] ).filter( v => v.DocumentId !== version.DocumentId ),
            } : prev );

            setRefreshKey( prev => prev + 1 );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to delete document.';
            setLoadingError( message );
        } finally {
            setDeletingId( null );
        }
    };

    const handleDownload = async ( item: DocumentVersionRow ) => {
        // SharePoint versions aren't in blob storage; open the file where it lives.
        if( item.SharepointLink ){
            setLoadingError( '' );
            window.open( item.SharepointLink, '_blank', 'noopener,noreferrer' );
            return;
        }

        if( !item.DocumentId ){
            setLoadingError( 'Unable to download document. Missing document id.' );
            return;
        }

        setDownloadingId( item.DocumentId );
        setLoadingError( '' );

        try {
            const blob = await downloadDocumentFromStorage( clientId, item.DocumentId );
            const { name, extension } = getDocumentFileParts( item );
            triggerBrowserDownload( blob, extension ? `${ name }.${ extension }` : name );
        } catch( err ){
            const message = err instanceof Error ? err.message : 'Failed to download document.';
            setLoadingError( message );
        } finally {
            setDownloadingId( null );
        }
    };

    return (
        <div className={ 'document-version-mini' }>
            { loadingError && (
                <Message
                    negative
                    icon={ 'warning circle' }
                    className={ 'document-version-mini-error' }
                    content={ loadingError }
                />
            ) }

            { uploadError && (
                <Message
                    negative
                    icon={ 'warning circle' }
                    className={ 'document-version-mini-message' }
                    content={ uploadError }
                />
            ) }

            <div className={ 'document-version-mini-list' }>
                { loading && (
                    <div className={ 'document-version-mini-empty' }>
                        <Icon name={ 'spinner' } loading /> Loading documents...
                    </div>
                ) }

               

                { !loading && documents.map( item => {
                    const rowKey = item.DocumentItemId || item.DocumentVersionId || item.DocumentId || `${ item.FileName || item.DocumentName || 'doc' }-${ item.VersionNumber || 0 }`;
                    const isDragOver = !!item.DocumentItemId && dragOverItemId === item.DocumentItemId;
                    const itemVersions = item.Versions || [];

                    return (
                        <div
                            key={ rowKey }
                            className={ `document-version-mini-row ${ isDragOver ? 'is-drag-over' : '' }` }
                            draggable={ !!item.DocumentId }
                            onDragStart={ event => {
                                if( !item.DocumentId ) return;
                                // DocumentId is the DocumentItem's active version; the name is the DocumentItem's
                                // (same label the row shows), not the underlying file name.
                                const fileParts = getDocumentFileParts( item );
                                setAIContextDragData( event.dataTransfer, {
                                    documentId: item.DocumentId,
                                    name: fileDisplayName || item.DocumentName || item.DocumentItemName || fileParts.name,
                                    extension: fileParts.extension,
                                } );
                            } }
                            onDragOver={ event => handleDragOver( event, item.DocumentItemId ) }
                            onDragLeave={ event => handleDragLeave( event, item.DocumentItemId ) }
                            onDrop={ event => handleDrop( event, item.DocumentItemId ) }
                        >
                            <span className={ 'document-version-mini-row-main' }>
                                <span className={ 'document-version-mini-row-name' }>
                                    { item.SharepointLink && <SharepointBadge /> }
                                    { fileDisplayName || item.DocumentName || item.DocumentItemName || item.FileName || item.DocumentId || 'Unnamed document' }
                                </span>
                                <span className={ 'document-version-mini-row-meta' }>
                                    { item.VersionNumber != null ? `v${ item.VersionNumber }` : 'No version' }
                                    { item.IsActiveDocumentVersion === 1 ? ' · Active' : '' }
                                    { item.CompletedDate ? ` · ${ formatUpdatedUtc( item.CompletedDate ) }` : '' }
                                </span>
                            </span>

                            <span className={ 'document-version-mini-row-actions' }>
                                <Button
                                    type={ 'button' }
                                    basic
                                    compact
                                    icon
                                    size={ 'mini' }
                                    className={ 'document-version-mini-action-btn' }
                                    title={ 'Upload new version' }
                                    onClick={ event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        openFilePickerForTarget( item.DocumentItemId );
                                    } }
                                >
                                    <Icon name={ 'upload' } />
                                </Button>

                                <Button
                                    type={ 'button' }
                                    basic
                                    compact
                                    icon
                                    size={ 'mini' }
                                    className={ 'document-version-mini-action-btn' }
                                    title={ 'Add SharePoint link as new version' }
                                    disabled={ !item.DocumentItemId }
                                    onClick={ event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        openSharepointDialog( item.DocumentItemId );
                                    } }
                                >
                                    <Icon name={ 'linkify' } />
                                </Button>

                                <Button
                                    type={ 'button' }
                                    basic
                                    compact
                                    icon
                                    size={ 'mini' }
                                    className={ 'document-version-mini-action-btn' }
                                    title={ item.SharepointLink ? 'Open active version in SharePoint' : 'Download active version' }
                                    disabled={ ( !item.DocumentId && !item.SharepointLink ) || ( !!item.DocumentId && downloadingId === item.DocumentId ) }
                                    onClick={ event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleDownload( item );
                                    } }
                                >
                                    { item.SharepointLink
                                        ? <Icon name={ 'external alternate' } />
                                        : <Icon name={ downloadingId === item.DocumentId ? 'spinner' : 'download' } loading={ downloadingId === item.DocumentId } /> }
                                </Button>

                                <Button
                                    type={ 'button' }
                                    basic
                                    compact
                                    icon
                                    size={ 'mini' }
                                    className={ 'document-version-mini-action-btn' }
                                    title={ 'Show all versions' }
                                    disabled={ itemVersions.length === 0 }
                                    onClick={ event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setVersionsOpenItem( item );
                                    } }
                                >
                                    <Icon name={ 'exclamation circle' } />
                                </Button>
                            </span>
                        </div>
                    );
                } ) }

                { !loading && showCreateItemRow && documents.length === 0 && (
                    <div
                        className={ `document-version-mini-row document-version-mini-row-add ${ dragOverItemId === '__new__' ? 'is-drag-over' : '' }` }
                        style={ {
                            position: 'relative',
                            overflow: 'visible',
                            paddingTop: miniLabel ? 14 : undefined,
                        } }
                        onDragOver={ event => {
                            event.preventDefault();
                            setDragOverItemId( '__new__' );
                        } }
                        onDragLeave={ event => {
                            event.preventDefault();
                            if( dragOverItemId === '__new__' ){
                                setDragOverItemId( null );
                            }
                        } }
                        onDrop={ event => {
                            event.preventDefault();
                            setDragOverItemId( null );
                            const file = event.dataTransfer.files?.[ 0 ];
                            if( file ){
                                openUploadConfirm( file, undefined, true );
                            }
                        } }
                    >
                        { !!miniLabel && (
                            <span
                                style={ {
                                    position: 'absolute',
                                    top: 0,
                                    left: 10,
                                    transform: 'translateY(-50%)',
                                    padding: '0 6px',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                    color: 'var(--color-purple)',
                                    background: '#fff',
                                    pointerEvents: 'none',
                                } }
                            >
                                { miniLabel }
                            </span>
                        ) }

                        <span className={ 'document-version-mini-row-main' }>
                            <span
                                className={ 'document-version-mini-row-name' }
                                style={ { fontSize: '1.02rem', fontWeight: 800, lineHeight: 1.25 } }
                            >
                                { fileDisplayName || 'Drop for new version' }
                            </span>
                        </span>
                        <span className={ 'document-version-mini-row-actions' }>
                            <Button
                                type={ 'button' }
                                basic
                                compact
                                icon
                                size={ 'mini' }
                                className={ 'document-version-mini-action-btn' }
                                title={ 'Upload new document' }
                                onClick={ event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    openFilePickerForTarget( undefined, true );
                                } }
                            >
                                <Icon name={ 'upload' } />
                            </Button>
                            <Button
                                type={ 'button' }
                                basic
                                compact
                                icon
                                size={ 'mini' }
                                className={ 'document-version-mini-action-btn' }
                                title={ 'Add SharePoint link' }
                                onClick={ event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    openSharepointDialog();
                                } }
                            >
                                <Icon name={ 'linkify' } />
                            </Button>
                        </span>
                    </div>
                ) }
            </div>

            <input
                ref={ fileInputRef }
                type={ 'file' }
                style={ { display: 'none' } }
                onChange={ handleFileInputChange }
            />

            <Modal
                size={ 'small' }
                open={ isUploadConfirmOpen }
                onClose={ closeUploadConfirm }
                closeOnDimmerClick={ !isUploading }
                closeOnEscape={ !isUploading }
            >
                <Header icon={ 'file outline' } content={ 'Confirm document upload' } />
                <Modal.Content>
                    { pendingUpload && (
                        <div className={ 'document-version-mini-confirm-details' }>
                            <p><strong>File:</strong> { pendingUpload.file.name }</p>
                            <p><strong>Type:</strong> { pendingUpload.file.type || 'Unknown' }</p>
                            <p><strong>Size:</strong> { formatFileSize( pendingUpload.file.size ) }</p>
                            <p><strong>ClientId:</strong> { clientId }</p>
                            { pendingUpload.targetDocumentItemId && (
                                <p><strong>DocumentItemId:</strong> { pendingUpload.targetDocumentItemId }</p>
                            ) }

                            { pendingUpload.requireDocumentName && (
                                <div className={ 'document-version-mini-name-input-wrap' }>
                                    <label htmlFor={ 'document-version-mini-name-input' }>
                                        Document Name
                                    </label>
                                    <input
                                        id={ 'document-version-mini-name-input' }
                                        type={ 'text' }
                                        value={ documentName }
                                        onChange={ e => setDocumentName( e.target.value ) }
                                        placeholder={ 'Enter document name' }
                                    />
                                </div>
                            ) }
                        </div>
                    ) }
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={ closeUploadConfirm }
                        disabled={ isUploading }
                        content={ 'Cancel' }
                    />
                    <Button
                        positive
                        icon={ 'upload' }
                        labelPosition={ 'right' }
                        content={ isUploading ? 'Uploading...' : 'Confirm and Upload' }
                        loading={ isUploading }
                        disabled={ isUploading || !pendingUpload }
                        onClick={ handleConfirmUpload }
                    />
                </Modal.Actions>
            </Modal>

            <SharepointLinkDialog
                open={ !!sharepointTarget }
                requireDocumentName={ sharepointTarget?.requireDocumentName }
                onClose={ () => setSharepointTarget( null ) }
                onSubmit={ handleSubmitSharepointLink }
            />

            <Modal
                size={ 'small' }
                open={ !!versionsOpenItem }
                onClose={ () => setVersionsOpenItem( null ) }
                closeIcon
                className={ 'document-version-mini-versions-modal' }
            >
                <Header
                    icon={ 'history' }
                    content={ versionsOpenItem?.DocumentName || versionsOpenItem?.DocumentItemName || 'Document Versions' }
                />
                <Modal.Content>
                    <div className={ 'document-version-mini-popup-list' }>
                        { ( versionsOpenItem?.Versions || [] ).length === 0 && (
                            <div className={ 'document-version-mini-empty' }>
                                No versions available.
                            </div>
                        ) }

                        { ( versionsOpenItem?.Versions || [] ).map( version => {
                            const versionKey = version.DocumentVersionId || version.DocumentId || `${ version.DocumentName || 'version' }-${ version.VersionNumber || 0 }`;
                            return (
                                <div
                                    key={ versionKey }
                                    className={ 'document-version-mini-popup-row' }
                                >
                                    <span className={ 'document-version-mini-row-main' }>
                                        <span className={ 'document-version-mini-row-name' }>
                                            { version.SharepointLink && <SharepointBadge /> }
                                            { version.FileName || version.DocumentName || version.DocumentId || 'Unnamed version' }
                                        </span>
                                        <span className={ 'document-version-mini-row-meta' }>
                                            <span className={ 'document-version-popup-badge' }>
                                                { version.VersionNumber != null ? `v${ version.VersionNumber }` : 'No version' }
                                            </span>
                                            { version.IsActiveDocumentVersion === 1 && (
                                                <span className={ 'document-version-popup-badge document-version-popup-badge-active' }>
                                                    Active
                                                </span>
                                            ) }
                                            { version.CompletedByConsultantName ? <span>{ version.CompletedByConsultantName }</span> : null }
                                            { version.CompletedDate ? <span>{ formatUpdatedUtc( version.CompletedDate ) }</span> : null }
                                        </span>
                                    </span>
                                    <span className={ 'document-version-mini-row-actions' }>
                                        <Button
                                            type={ 'button' }
                                            basic
                                            compact
                                            icon
                                            size={ 'mini' }
                                            className={ 'document-version-mini-action-btn' }
                                            title={ version.SharepointLink ? 'Open this version in SharePoint' : 'Download this version' }
                                            disabled={ ( !version.DocumentId && !version.SharepointLink ) || ( !!version.DocumentId && downloadingId === version.DocumentId ) }
                                            onClick={ () => handleDownload( version ) }
                                        >
                                            { version.SharepointLink
                                                ? <Icon name={ 'external alternate' } />
                                                : <Icon name={ downloadingId === version.DocumentId ? 'spinner' : 'download' } loading={ downloadingId === version.DocumentId } /> }
                                        </Button>
                                        <Button
                                            type={ 'button' }
                                            basic
                                            compact
                                            icon
                                            size={ 'mini' }
                                            className={ 'document-version-mini-action-btn' }
                                            title={ 'Delete this version' }
                                            disabled={ !version.DocumentId || deletingId === version.DocumentId }
                                            onClick={ () => handleDeleteVersion( version ) }
                                        >
                                            <Icon name={ deletingId === version.DocumentId ? 'spinner' : 'trash' } loading={ deletingId === version.DocumentId } />
                                        </Button>
                                    </span>
                                </div>
                            );
                        } ) }
                    </div>
                </Modal.Content>
            </Modal>
        </div>
    );
};

export default DocumentVersionMini;